// lib
import { useContext, useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import ADND from '@aytm/lib/adnd/ADND'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

import { DataContext } from '@aytm/app/stats/page/question-views/vq/Component'

import { generateUID, encodeText, decodeText } from '@aytm/app/stats/lib/util/helpers'
import { getLogicData, getRefs, getClustersRefs } from '@aytm/app/stats/lib/query/refs'

// components
import { InputText } from 'primereact/inputtext'

import TextPopup from '@aytm/lib/rfd-ui/components/basic/text-popup'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

import AdvancedFilterContainer from '@aytm/app/stats/components/advanced-filter/AdvancedFilterContainer'

import AnswerVariantOptions from '@aytm/app/stats/page/question-views/vq/components/AnswerVariantOptions'

import ImagesDropdown from '@aytm/app/stats/components/images-dropdown/ImagesDropdown'

// hooks
import useSingleAndDoubleClick from '@aytm/app/stats/page/components/hooks/useSingleAndDoubleClick'

// styles
import style from '@aytm/app/stats/page/question-views/vq/styles/Component.module.scss'

const AnswerVariantMX = ({ item, position, dndItemsList }) => {
  const { id, data, disabled, dispatch, dropdownsRef, parserResultEof } = useContext(DataContext)

  const [baseMode, setBaseMode] = useState(false)

  const [basePattern, setBasePattern] = useState(item.base || '')

  const [selectedValue, setSelectedValue] = useState(null)

  const changedRef = useRef(0 < item.pattern.trim().length)

  const itemPatternRef = useRef(null)
  itemPatternRef.current = item.pattern

  const dndListItemRef = useRef()
  const dndListItemHandleRef = useRef()

  const parserResultPattern = useMemo(
    () =>
      data.parser.analyzeString({
        id: id,
        filter: item.pattern.trim(),
        clusters: data.clusters,
        saveTokens: true
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [item.pattern, id, data.surveySchema, data.clusters]
  )
  const parserResultPatternError = useMemo(
    () => 0 < item.pattern.trim().length && 0 < parserResultPattern.expression.status.errors.length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [parserResultPattern]
  )

  const [parserResultPatternBasic, setParserResultPatternBasic] = useState({})

  useEffect(() => {
    const tree = parserResultPatternError
      ? null
      : 0 < item.pattern.trim().length
      ? 0 < parserResultPattern.expression.status.errors.length
        ? null
        : { [item.pattern]: { x: true } }
      : {}
    setParserResultPatternBasic(null === tree || 1 < Object.keys(tree).length ? null : tree)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parserResultPattern, parserResultPatternError])

  const parserResultBase = useMemo(
    () =>
      data.parser.analyzeString({
        id: id,
        filter: basePattern,
        clusters: data.clusters,
        saveTokens: true,
        numberAllowed: true
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, data.surveySchema, data.clusters, basePattern]
  )
  const parserResultBaseError = useMemo(
    () =>
      0 < basePattern.length &&
      0 < parserResultBase.expression.status.errors.length &&
      parserResultBase.expression.status.errors.some(v => !['ref', 'cluster'].includes(v.type)),
    [basePattern, parserResultBase]
  )

  const textBase = useMemo(
    () =>
      (30 < item.base.length && 'Base: ' + item.base.slice(0, 27) + '...') ||
      (0 < item.base.length && 'Base: ' + item.base) ||
      'Base',
    [item.base]
  )

  const fetchData = useCallback(() => {
    return {
      id,
      item,
      position: position - 1,
      windowUID: PageGlobals.WINDOW_UID,
      remove: () =>
        dispatch({
          type: 'DELETE_ANSWER_VARIANT',
          data: position - 1
        })
    }
  }, [item, position, id, dispatch])

  const dndListItem = ADND.targets.itemsList.listItem({
    type: `dlq-answer-${PageGlobals.WINDOW_UID}`,
    list: dndItemsList
  })
  const dndListItemHandle = ADND.sources
    .listItemHandle({
      type: `dlq-answer-${PageGlobals.WINDOW_UID}`,
      item: dndListItem,
      cloneable: true
    })
    .fetchData(fetchData)

  const onSelectedChange = (value = null, _values) => {
    const group = data.options.find(v => v.items.some(v => v.key == value))
    if (group.disabled) return

    const option = group.items.find(v => v.key == value) || null

    setSelectedValue(value)
    setBasePattern(value)

    setBaseMode(false)
    setBasePattern(value)

    changedRef.current = true

    dispatch({
      type: 'UPDATE_ANSWER_VARIANT',
      data: {
        item: {
          ...item,
          pattern: null === option ? '' : value,
          base: null === option ? '' : value
        },
        position: position - 1
      }
    })
  }

  const onLabelChange = event => {
    dispatch({
      type: 'UPDATE_ANSWER_VARIANT',
      data: {
        item: {
          ...item,
          text: encodeText(event.target.value)
        },
        position: position - 1
      }
    })
  }

  const onBaseChange = value => {
    setBaseMode(false)
    setBasePattern(value)

    item.base != value &&
      dispatch({
        type: 'UPDATE_ANSWER_VARIANT',
        data: {
          item: {
            ...item,
            base: value
          },
          position: position - 1
        }
      })
  }

  const onImageChange = value => {
    if ('off' == value) delete item.image
    else item.image = value

    dispatch({
      type: 'UPDATE_ANSWER_VARIANT',
      data: {
        item: {
          ...item
        },
        position: position - 1
      }
    })
  }

  const onDeleteClick = () => {
    dispatch({
      type: 'DELETE_ANSWER_VARIANT',
      data: position - 1
    })
  }

  const onHandleClick = useSingleAndDoubleClick(null, () => {
    dispatch({
      type: 'ADD_ANSWER_VARIANT',
      data: {
        item: {
          ...item,
          key: generateUID()
        },
        position: position
      }
    })
  })

  useEffect(() => {
    dropdownsRef.current[position] = selectedValue

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      delete dropdownsRef.current[position]
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position, selectedValue]) // TODO

  useEffect(() => {
    if (changedRef.current) {
      changedRef.current = false
      return
    }

    const options = data.options.flatMap(v => v.items)
    const option =
      options.find(v => String(v.value || v.key).toLowerCase() == itemPatternRef.current.toLowerCase()) || null

    if (null === option) {
      setSelectedValue(null)
    } else {
      setSelectedValue(option.key)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parserResultPatternBasic])

  useEffect(() => {
    if (dndListItem) dndListItem.connect(dndListItemRef.current)

    return () => {
      if (dndListItem) dndListItem.disconnect()
    }
  }, [dndListItem])

  useEffect(() => {
    if (dndListItemHandle) dndListItemHandle.connect(dndListItemHandleRef.current)

    return () => {
      if (dndListItemHandle) dndListItemHandle.disconnect()
    }
  }, [dndListItemHandle])

  useEffect(() => {
    dispatch({
      type: 'UPDATE_LOGIC_DATA',
      data: {
        id: `a${position}.pattern`,
        logicData: getLogicData(data.tags, data.codeBooks, parserResultPattern, parserResultPatternError)
      }
    })

    return () => {
      dispatch({
        type: 'UPDATE_LOGIC_DATA',
        data: {
          id: `a${position}.pattern`,
          logicData: null
        }
      })
    }
  }, [position, data.tags, data.codeBooks, dispatch, parserResultPattern, parserResultPatternError])
  useEffect(() => {
    dispatch({
      type: 'UPDATE_LOGIC_DATA',
      data: {
        id: `a${position}.base`,
        logicData: getLogicData(data.tags, data.codeBooks, parserResultBase, parserResultBaseError)
      }
    })

    return () => {
      dispatch({
        type: 'UPDATE_LOGIC_DATA',
        data: {
          id: `a${position}.base`,
          logicData: null
        }
      })
    }
  }, [position, data.tags, data.codeBooks, dispatch, parserResultBase, parserResultBaseError])
  useEffect(() => {
    const patternRefs = getRefs(parserResultPattern)
    const baseRefs = getRefs(parserResultBase)
    const clustersRefs = getClustersRefs(data.modelClusters, parserResultPattern).concat(
      getClustersRefs(data.modelClusters, parserResultBase)
    )

    dispatch({
      type: 'UPDATE_REFS',
      data: {
        id: `a${position}`,
        refs: [patternRefs, baseRefs, clustersRefs]
      }
    })

    return () => {
      dispatch({
        type: 'UPDATE_REFS',
        data: {
          id: `a${position}`,
          refs: null
        }
      })
    }
  }, [position, data.modelClusters, dispatch, parserResultPattern, parserResultBase])
  useEffect(() => {
    dispatch({
      type: 'UPDATE_ERRORS',
      data: {
        id: `a${position}`,
        error: !item.pattern.trim().length || parserResultPatternError || parserResultBaseError
      }
    })

    return () => {
      dispatch({
        type: 'UPDATE_ERRORS',
        data: {
          id: `a${position}`,
          error: null
        }
      })
    }
  }, [item, position, dispatch, parserResultPatternError, parserResultBaseError])

  const renderOptions = () => (
    <AnswerVariantOptions
      position={position}
      value={selectedValue}
      options={data.options}
      dropdownsRef={dropdownsRef}
      onSelectedChange={onSelectedChange}
    />
  )

  const renderBaseMode = () => (
    <>
      <div className={`${style.base} p-fluid p-col-12`}>
        <AdvancedFilterContainer
          dataTestID={`stat-vq-answer-variant-pattern-${position}`}
          className={classNames('p-component p-inputtext p-inputtext-rounded p-inputtext-with-image', {
            'p-composite': !disabled,
            'p-composite-left': disabled
          })}
          pattern={basePattern}
          parser={data.parser}
          surveySchema={data.surveySchema}
          clusters={data.clusters}
          parserResultEof={parserResultEof}
          prefix="base = "
          hintPosition="top"
          options={{ numberAllowed: true }}
          readOnly={disabled}
          onChange={disabled ? null : setBasePattern}
        />
      </div>
      <span
        data-testid={`stat-vq-answer-variant-base-clear-${position}`}
        className={style.clear}
        aria-label="Clear base"
        title="Clear"
        {...pressable(disabled ? null : () => onBaseChange(''))}
      >
        <i className="icon-aytmfd-x-v2" />
      </span>
      <span
        data-testid={`stat-vq-answer-variant-base-apply-${position}`}
        className={style.apply}
        aria-label="Apply base"
        title="Apply"
        {...pressable(disabled || parserResultBaseError ? null : () => onBaseChange(basePattern.trim()))}
      >
        <i className="icon-aytmfd-checkmark" />
      </span>
    </>
  )
  const renderLabelMode = () => (
    <>
      {disabled && !item.base.length ? null : (
        <span
          data-testid={`stat-vq-answer-variant-base-toggle-${position}`}
          className={`${style['group-toggle']}${disabled ? ' ' + style['group-toggle-disabled'] : ''}`}
          {...pressable(disabled ? null : () => setBaseMode(true))}
        >
          {30 < item.base.length ? (
            <TextPopup text={textBase} content={item.base} direction="bottomright" portal />
          ) : (
            textBase
          )}
        </span>
      )}
      <div className={`${style.images} p-col`}>
        <InputText
          className={classNames('p-inputtext-rounded p-inputtext-with-image', {
            'p-composite': !disabled,
            'p-composite-left': disabled
          })}
          placeholder="Please type a label..."
          value={decodeText(item.text)}
          maxLength={120}
          disabled={disabled}
          onChange={disabled ? null : onLabelChange}
        />
        <ImagesDropdown image={item.image} options={data.images} disabled={disabled} onImageChange={onImageChange} />
      </div>
    </>
  )
  const renderModes = () => (baseMode ? renderBaseMode() : renderLabelMode())

  return (
    <div
      data-testid={`stat-vq-answer-variant-${position}`}
      ref={dndListItemRef}
      className={`PRAYTMTheme ${style.row} ${style['row-av']}`}
    >
      {disabled ? null : (
        <span
          data-testid={`stat-vq-answer-variant-delete-${position}`}
          className={style.delete}
          aria-label="Delete answer variant"
          {...pressable(onDeleteClick)}
        >
          <i className="icon-aytmfd-x-v2" />
        </span>
      )}
      <div className={style.index}>SQ{position}.</div>
      <div className={`${style.group} ${style['group-av']}${disabled ? ' ' + style['group-av-disabled'] : ''} p-col`}>
        <div className="p-fluid p-col-4">{renderOptions()}</div>
        <div className={`${style.group} p-fluid p-col`}>{renderModes()}</div>
      </div>
      {disabled ? null : (
        <span
          data-testid={`stat-vq-answer-variant-handle-${position}`}
          ref={dndListItemHandleRef}
          className={style.handle}
          onClick={onHandleClick}
        />
      )}
    </div>
  )
}

AnswerVariantMX.propTypes = {
  item: PropTypes.object,
  position: PropTypes.number,
  dndItemsList: PropTypes.any
}

AnswerVariantMX.displayName = 'AnswerVariantMX'

export default AnswerVariantMX
